import type {
  CalendarDate,
  CalendarDay,
  DatePickerCurrentYearPosition,
  DatePickerValue,
} from "./DatePicker.types";

export const DEFAULT_DATE_PICKER_LOCALE = "es-AR";
export const DEFAULT_DATE_PICKER_TIME_ZONE =
  "America/Argentina/Buenos_Aires";

export const MONTHS = Array.from({ length: 12 }, (_, index) => index + 1);

export const WEEKDAY_LABELS = ["D", "L", "M", "M", "J", "V", "S"] as const;

export const getVisibleYears = (
  currentYear: number,
  currentYearPosition: DatePickerCurrentYearPosition = "center",
): number[] => {
  if (currentYearPosition === "only") return [currentYear];

  const offsetByPosition: Record<
    Exclude<DatePickerCurrentYearPosition, "only">,
    number
  > = {
    left: 0,
    center: -1,
    right: -2,
  };
  const firstYear = currentYear + offsetByPosition[currentYearPosition];

  return Array.from({ length: 3 }, (_, index) => firstYear + index);
};

export const isYearWithinBounds = (
  year: number,
  minDate?: CalendarDate,
  maxDate?: CalendarDate,
): boolean =>
  (!minDate || year >= minDate.year) && (!maxDate || year <= maxDate.year);

export const getDaysInMonth = (year: number, month: number): number =>
  new Date(Date.UTC(year, month, 0)).getUTCDate();

export const compareCalendarDates = (
  left: CalendarDate,
  right: CalendarDate,
): number => {
  if (left.year !== right.year) return left.year - right.year;
  if (left.month !== right.month) return left.month - right.month;
  return left.day - right.day;
};

export const clampCalendarDateToBounds = (
  date: CalendarDate,
  minDate?: CalendarDate,
  maxDate?: CalendarDate,
): CalendarDate => {
  const normalizedDate = {
    ...date,
    day: clampDay(date.year, date.month, date.day),
  };

  if (minDate && compareCalendarDates(normalizedDate, minDate) < 0) {
    return minDate;
  }
  if (maxDate && compareCalendarDates(normalizedDate, maxDate) > 0) {
    return maxDate;
  }

  return normalizedDate;
};

export const isDateWithinBounds = (
  date: CalendarDate,
  minDate?: CalendarDate,
  maxDate?: CalendarDate,
): boolean =>
  (!minDate || compareCalendarDates(date, minDate) >= 0) &&
  (!maxDate || compareCalendarDates(date, maxDate) <= 0);

export const isMonthWithinBounds = (
  year: number,
  month: number,
  minDate?: CalendarDate,
  maxDate?: CalendarDate,
): boolean => {
  const firstDay = { year, month, day: 1 };
  const lastDay = { year, month, day: getDaysInMonth(year, month) };

  return (
    (!minDate || compareCalendarDates(lastDay, minDate) >= 0) &&
    (!maxDate || compareCalendarDates(firstDay, maxDate) <= 0)
  );
};

export const buildCalendarDays = (
  year: number,
  month: number,
): CalendarDay[] => {
  const firstWeekday = new Date(Date.UTC(year, month - 1, 1)).getUTCDay();

  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(Date.UTC(year, month - 1, index - firstWeekday + 1));

    return {
      year: date.getUTCFullYear(),
      month: date.getUTCMonth() + 1,
      day: date.getUTCDate(),
      isCurrentMonth: date.getUTCMonth() + 1 === month,
    };
  });
};

export const getCalendarDateInTimeZone = (
  date: Date = new Date(),
  timeZone: string = DEFAULT_DATE_PICKER_TIME_ZONE,
): CalendarDate => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    timeZone,
  }).formatToParts(date);
  const values = Object.fromEntries(
    parts
      .filter((part) => part.type !== "literal")
      .map((part) => [part.type, Number(part.value)]),
  );

  return {
    year: values.year,
    month: values.month,
    day: values.day,
  };
};

export const getCurrentMonthValue = (
  date: Date = new Date(),
  timeZone: string = DEFAULT_DATE_PICKER_TIME_ZONE,
): DatePickerValue => {
  const current = getCalendarDateInTimeZone(date, timeZone);
  return { mode: "month", year: current.year, month: current.month };
};

const capitalize = (value: string) =>
  value.charAt(0).toUpperCase() + value.slice(1);

export const formatMonthName = (
  month: number,
  locale: string = DEFAULT_DATE_PICKER_LOCALE,
  format: "short" | "long" = "short",
): string => {
  const label = new Intl.DateTimeFormat(locale, {
    month: format,
    timeZone: "UTC",
  })
    .format(new Date(Date.UTC(2024, month - 1, 1)))
    .replace(".", "");

  return capitalize(label);
};

export const formatDatePickerValue = (
  value: DatePickerValue,
  locale: string = DEFAULT_DATE_PICKER_LOCALE,
): string => {
  const month = formatMonthName(value.month, locale, "short");

  return value.mode === "month"
    ? `${month} ${value.year}`
    : `${value.day} ${month} ${value.year}`;
};

export const clampDay = (year: number, month: number, day: number): number =>
  Math.min(Math.max(day, 1), getDaysInMonth(year, month));

export const isSameCalendarDate = (
  left: CalendarDate,
  right: CalendarDate,
): boolean => compareCalendarDates(left, right) === 0;
